import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Database, ArrowRight, User, Lock, ChevronsUpDown } from 'lucide-react'

interface LoginPageProps {
  onLogin: (username: string) => void
  version?: string
  onVersionChange?: (version: string) => void
}

const DEFAULT_USERNAMES = [
  '海珠中支公司部经理',
  '海珠中支行长',
  '昌岗路支行行长',
  '金融科技部运营',
]

export function LoginPage({ onLogin, version, onVersionChange, onOpenMobile }: LoginPageProps) {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [showDropdown, setShowDropdown] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // 点击外部关闭下拉
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleLogin = () => {
    const loginName = username || 'eagle'
    onLogin(loginName)
  }

  const selectUsername = (name: string) => {
    setUsername(name)
    setShowDropdown(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 via-blue-500 to-blue-400 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-md">
        {/* Logo & Title */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-gcb-primary to-gcb-primaryLight rounded-2xl flex items-center justify-center shadow-lg mb-4">
            <Database className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">智数坊</h1>
          <p className="text-sm text-gray-500 mt-2">广州银行</p>
        </div>

        {/* Description */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
          <p className="text-sm text-gray-600 leading-relaxed">
            整合全行业务主题数据资产，提供一站式数据搜索、分析、报表制作服务。
          </p>
        </div>

        {/* Username */}
        <div className="mb-4" ref={dropdownRef}>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="请输入用户名"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="h-11 pl-10 pr-4 text-sm rounded-lg border-gray-300 focus:border-gcb-primary focus:ring-2 focus:ring-gcb-primary/20"
            />
            {/* Default usernames dropdown */}
            {showDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                <div className="px-3 py-1.5 text-[10px] text-gray-400 border-b bg-gray-50 flex items-center gap-1">
                  <User className="w-2.5 h-2.5" /> 快捷登录
                </div>
                {DEFAULT_USERNAMES.map((name, idx) => (
                  <button
                    key={`${name}-${idx}`}
                    onClick={() => selectUsername(name)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-gcb-primary transition-colors truncate"
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Password */}
        <div className="mb-6">
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              type="password"
              placeholder="请输入密码"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
              className="h-11 pl-10 pr-4 text-sm rounded-lg border-gray-300 focus:border-gcb-primary focus:ring-2 focus:ring-gcb-primary/20"
            />
          </div>
        </div>

        {/* Version Selector */}
        <div className="mb-6">
          <div className="relative">
            <ChevronsUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <select
              value={version || '1'}
              onChange={(e) => onVersionChange?.(e.target.value)}
              className="h-11 w-full text-sm rounded-lg border border-gray-300 pl-10 pr-4 appearance-none bg-white focus:border-gcb-primary focus:ring-2 focus:ring-gcb-primary/20 cursor-pointer"
            >
              <option value="1">v1.0</option>
              <option value="2">v2.0</option>
            </select>
          </div>
        </div>

        {/* Login Button */}
        <Button
          onClick={handleLogin}
          className="w-full h-12 bg-gradient-to-r from-gcb-primary to-gcb-primaryLight hover:from-gcb-primaryDark hover:to-gcb-primary text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
        >
          登录
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {/* Footer */}
        <p className="text-xs text-gray-400 mt-6 text-center">
          数据驱动决策 · 智慧赋能业务
        </p>
      </div>
    </div>
  )
}
